/**
 * Servicio para consultar pedidos y extraer guías para el proceso manual de Envía Colvanes
 */
import axiosInstance from '../../api/axiosInstance'
import type { OrderWithGuide } from '../../types/cron/colvanesManual'
import type { Order } from '../../types/order'

interface ListResponse {
  data: Order[]
  meta: {
    currentPage: number
    totalPages: number
    totalItems: number
    statistics?: any
  }
}

export class ColvanesManualService {
  private static readonly TARGET_STATES = ['enviado', 'on_route', 'EnReparto']
  private static readonly LIMIT_PER_PAGE = 100

  /**
   * Obtener todos los pedidos con guías de Colvanes
   * Filtra por: transportadora = "envia" O (transportadora es null/undefined Y guía empieza con "03")
   * Estados: "enviado", "on_route" y "EnReparto"
   */
  static async getAllColvanesOrders(
    onProgress?: (progress: {
      currentPage: number
      totalPages: number
      ordersFetched: number
    }) => void
  ): Promise<OrderWithGuide[]> {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      throw new Error('No se encontró token de autenticación')
    }

    const allOrders: OrderWithGuide[] = []
    let currentPage = 1
    let totalPages = 1

    try {
      // Primera consulta para obtener totalPages
      const firstResponse = await axiosInstance.get<ListResponse>(
        `/list?page=${currentPage}&limit=${this.LIMIT_PER_PAGE}&states=${this.TARGET_STATES.join(',')}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      const firstData = firstResponse.data.data
      totalPages = firstResponse.data.meta.totalPages

      // Extraer guías de la primera página
      const firstPageGuides = this.extractGuidesFromOrders(firstData)
      allOrders.push(...firstPageGuides)

      // Notificar progreso
      if (onProgress) {
        onProgress({
          currentPage: 1,
          totalPages,
          ordersFetched: firstData.length,
        })
      }

      // Si hay más páginas, consultarlas todas
      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page++) {
          const response = await axiosInstance.get<ListResponse>(
            `/list?page=${page}&limit=${this.LIMIT_PER_PAGE}&states=${this.TARGET_STATES.join(',')}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )

          const pageData = response.data.data
          const pageGuides = this.extractGuidesFromOrders(pageData)
          allOrders.push(...pageGuides)

          // Notificar progreso
          if (onProgress) {
            onProgress({
              currentPage: page,
              totalPages,
              ordersFetched: allOrders.length,
            })
          }
        }
      }

      return allOrders
    } catch (error: any) {
      console.error('Error obteniendo pedidos Colvanes:', error)
      throw new Error(
        error.response?.data?.message ||
          `Error al obtener pedidos: ${error.message}`
      )
    }
  }

  /**
   * Extraer guías de un array de pedidos
   * Requiere guía. Incluye pedidos con transportadora envia/Envía/envía
   * o sin transportadora y guía que empieza con "03"
   */
  private static extractGuidesFromOrders(orders: Order[]): OrderWithGuide[] {
    const guides: OrderWithGuide[] = []

    for (const order of orders) {
      if (!order.guia?.trim()) {
        continue
      }

      const guiaTrimmed = order.guia.trim()
      const transportadora = (order as any).transportadora

      // Criterio 1: Si tiene transportadora = "envia"
      if (transportadora === 'envia' || transportadora === 'Envía' || transportadora === 'envía') {
        guides.push({
          guia: guiaTrimmed,
          orders_id: order.orders_id ?? undefined,
          store_name: order.store_name,
          id: order.id,
          transportadora: transportadora,
        })
        continue
      }

      // Criterio 2: Si transportadora es null/undefined Y la guía empieza con "03"
      if (
        (!transportadora || transportadora === null || transportadora === '') &&
        guiaTrimmed.startsWith('03')
      ) {
        guides.push({
          guia: guiaTrimmed,
          orders_id: order.orders_id ?? undefined,
          store_name: order.store_name,
          id: order.id,
          transportadora: transportadora || undefined,
        })
      }
    }

    return guides
  }
}

